/**
 * @fileoverview Funções puras de cálculo de métricas a partir do
 * dataset primitivo (conversations + messages).
 *
 * Todas as funções recebem `{ conversations, messages, now }` (subset
 * já filtrado por subsidiária/SC/período) e retornam o número/objeto.
 *
 * Convenções:
 *   - Tempos retornados em SEGUNDOS (number). Formatação HH:MM:SS na UI.
 *   - "casos em aberto" = conversation.endedAt == null
 *   - "sem resposta" = status === "no_reply" (só client mandou)
 *   - "aguardando cliente" = status === "waiting_client" (último msg foi do agente)
 *   - "aguardando agente" = status === "waiting_agent" (último msg foi do cliente, agente já tinha respondido)
 *
 * Cada função é defensiva: array vazio → 0 ou null (NUNCA NaN).
 */

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

// ── Helpers ───────────────────────────────────────────────────────────
const sumTimes = (arr) => arr.reduce((a, b) => a + b, 0);

const avgSec = (arr) => {
  if (!arr.length) return 0;
  return Math.round(sumTimes(arr) / arr.length / 1000);
};

const maxSec = (arr) => {
  if (!arr.length) return 0;
  return Math.round(Math.max(...arr) / 1000);
};

const startOfDay = (ts) => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const startOfMonth = (ts) => {
  const d = new Date(ts);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// ── KPI individuais ───────────────────────────────────────────────────

/** Casos em aberto sem resposta (count) — só conversa que ninguém respondeu */
export const openNoReplyCount = (conversations) => {
  let n = 0;
  for (const c of conversations) {
    if (c.endedAt == null && c.status === "no_reply") n++;
  }
  return n;
};

/**
 * Maior tempo (em segundos) de caso aberto sem resposta do atendente.
 * Considera todas as conversas abertas onde o agente ainda não respondeu
 * (status no_reply). Tempo = now - firstClientAt.
 * Retorna { seconds, smartCenterId } (pra preencher card "Operação com maior tempo").
 */
export const longestNoReplyAttendant = (conversations, now) => {
  let best = 0;
  let bestScId = null;
  const nowMs = now.getTime();
  for (const c of conversations) {
    if (c.endedAt != null) continue;
    if (c.status !== "no_reply") continue;
    const dur = nowMs - c.firstClientAt;
    if (dur > best) {
      best = dur;
      bestScId = c.smartCenterId;
    }
  }
  return { seconds: Math.round(best / 1000), smartCenterId: bestScId };
};

/**
 * Tempo médio de 1ª resposta DO ATENDENTE (segundos).
 * Para cada conversa com pelo menos 1 mensagem do agente:
 *   delay = firstAgentReplyAt - firstClientAt
 * Média do delay.
 */
export const avgFirstReplyAttendantSec = (conversations) => {
  const deltas = [];
  for (const c of conversations) {
    if (c.firstAgentReplyAt == null) continue;
    if (c.firstClientAt == null) continue;
    const d = c.firstAgentReplyAt - c.firstClientAt;
    if (d > 0) deltas.push(d);
  }
  return avgSec(deltas);
};

/**
 * Maior tempo de primeira resposta (segundos) — pior caso registrado de
 * delay entre primeira msg do cliente e primeira resposta do agente.
 * Retorna { seconds, smartCenterId }.
 */
export const longestFirstReply = (conversations) => {
  let best = 0;
  let bestScId = null;
  for (const c of conversations) {
    if (c.firstAgentReplyAt == null) continue;
    const d = c.firstAgentReplyAt - c.firstClientAt;
    if (d > best) {
      best = d;
      bestScId = c.smartCenterId;
    }
  }
  return { seconds: Math.round(best / 1000), smartCenterId: bestScId };
};

/**
 * Tempo médio de 1ª resposta DO CLIENTE (segundos).
 * Para cada conversa com pelo menos 2 mensagens do cliente (uma inicial +
 * uma após resposta do agente):
 *   delay = secondClientAt - firstAgentReplyAt
 */
export const avgFirstReplyClientSec = (conversations) => {
  const deltas = [];
  for (const c of conversations) {
    if (c.secondClientAt == null) continue;
    if (c.firstAgentReplyAt == null) continue;
    const d = c.secondClientAt - c.firstAgentReplyAt;
    if (d > 0) deltas.push(d);
  }
  return avgSec(deltas);
};

/** Total de casos em aberto (count) */
export const totalOpenCount = (conversations) => {
  let n = 0;
  for (const c of conversations) if (c.endedAt == null) n++;
  return n;
};

/** Casos aguardando resposta do cliente (count) */
export const waitingClientCount = (conversations) => {
  let n = 0;
  for (const c of conversations) {
    if (c.endedAt == null && c.status === "waiting_client") n++;
  }
  return n;
};

/**
 * Tempo médio total de interação por cliente (segundos).
 * Para cada conversa FECHADA: endedAt - startedAt.
 */
export const avgInteractionTimeSec = (conversations) => {
  const durs = [];
  for (const c of conversations) {
    if (c.endedAt == null) continue;
    const d = c.endedAt - c.startedAt;
    if (d > 0) durs.push(d);
  }
  return avgSec(durs);
};

/** Total de atendimentos do dia (conversas iniciadas hoje) */
export const totalTodayCount = (conversations, now) => {
  const todayStart = startOfDay(now.getTime());
  const tomorrowStart = todayStart + DAY;
  let n = 0;
  for (const c of conversations) {
    if (c.startedAt >= todayStart && c.startedAt < tomorrowStart) n++;
  }
  return n;
};

// ── Período anterior (pra deltas vs. período anterior) ────────────────
/**
 * Aplica a mesma janela retrocedida em N dias e devolve a métrica.
 * Útil pra comparar "hoje vs. ontem", "últimos 7d vs. 7d anteriores", etc.
 */
export const inPriorWindow = (conversations, now, windowDays, fn) => {
  const nowMs = now.getTime();
  const fromCurrent = nowMs - windowDays * DAY;
  const fromPrior = fromCurrent - windowDays * DAY;
  // Prior: [fromPrior, fromCurrent)
  const subset = conversations.filter((c) => c.startedAt >= fromPrior && c.startedAt < fromCurrent);
  return fn(subset);
};

// ── KPI bundle (9 cards) ──────────────────────────────────────────────
/**
 * Calcula os 9 KPIs do header. Cada KPI tem:
 *   { key, label, value(display), description, delta? | note?, tooltip, icon, accent }
 */
export const computeKpis = ({ conversations, now, smartCentersById }) => {
  const fmtCount = (n) => Number(n).toLocaleString("pt-BR");
  const fmtHms = (sec) => {
    if (!Number.isFinite(sec) || sec < 0) return "—";
    const total = Math.round(sec);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // ── Cur and prior period: usamos 1 dia rolling pra delta padrão.
  const curOpenNoReply = openNoReplyCount(conversations);
  const priorOpenNoReply = inPriorWindow(conversations, now, 1, openNoReplyCount);
  const curAvgAttendant = avgFirstReplyAttendantSec(conversations);
  const priorAvgAttendant = inPriorWindow(conversations, now, 1, avgFirstReplyAttendantSec);
  const curAvgClient = avgFirstReplyClientSec(conversations);
  const priorAvgClient = inPriorWindow(conversations, now, 1, avgFirstReplyClientSec);
  const curTotalOpen = totalOpenCount(conversations);
  const priorTotalOpen = inPriorWindow(conversations, now, 1, totalOpenCount);
  const curWaitingClient = waitingClientCount(conversations);
  const priorWaitingClient = inPriorWindow(conversations, now, 1, waitingClientCount);
  const curInteraction = avgInteractionTimeSec(conversations);
  const priorInteraction = inPriorWindow(conversations, now, 1, avgInteractionTimeSec);
  const curTodayCount = totalTodayCount(conversations, now);
  const yesterdayConvs = conversations.filter((c) => {
    const yStart = startOfDay(now.getTime()) - DAY;
    const tStart = startOfDay(now.getTime());
    return c.startedAt >= yStart && c.startedAt < tStart;
  });
  const priorTodayCount = yesterdayConvs.length;

  const pctDelta = (cur, prior) => {
    if (!prior) return null;
    const pct = ((cur - prior) / prior) * 100;
    return Math.round(pct * 10) / 10;
  };

  const buildDelta = (cur, prior, lowerIsBetter = false) => {
    const pct = pctDelta(cur, prior);
    if (pct == null) return null;
    const direction = pct >= 0 ? "up" : "down";
    // "negative" = pior pra UX (vermelho)
    const negative = lowerIsBetter ? pct > 0 : pct < 0;
    return { pct: Math.abs(pct), direction, negative };
  };

  const longest = longestNoReplyAttendant(conversations, now);
  const longestFirst = longestFirstReply(conversations);

  const scName = (id) => smartCentersById?.get(id)?.name || "—";

  return [
    {
      key: "open_no_reply",
      label: "Casos em aberto sem resposta",
      value: fmtCount(curOpenNoReply),
      valueRaw: curOpenNoReply,
      description: "Total (somatória)",
      delta: buildDelta(curOpenNoReply, priorOpenNoReply, true),
      tooltip: "Conversas em aberto que ainda não receberam resposta do atendente.",
      icon: "MessageSquare",
      accent: "rose",
    },
    {
      key: "longest_no_reply_attendant",
      label: "Maior tempo de caso sem resposta",
      value: fmtHms(longest.seconds),
      valueRaw: longest.seconds,
      description: scName(longest.smartCenterId),
      delta: null,
      note: "Operação com maior tempo",
      tooltip: "Caso aberto há mais tempo sem resposta de atendente.",
      icon: "Timer",
      accent: "rose",
    },
    {
      key: "avg_first_reply_attendant",
      label: "Tempo médio de 1ª resposta do atendente",
      value: fmtHms(curAvgAttendant),
      valueRaw: curAvgAttendant,
      description: "Média geral",
      delta: buildDelta(curAvgAttendant, priorAvgAttendant, true),
      tooltip: "Quanto o cliente espera até receber a primeira resposta de um humano.",
      icon: "Users",
      accent: "emerald",
    },
    {
      key: "longest_first_reply",
      label: "Maior tempo de primeira resposta",
      value: fmtHms(longestFirst.seconds),
      valueRaw: longestFirst.seconds,
      description: scName(longestFirst.smartCenterId),
      delta: null,
      note: "Operação com maior tempo",
      tooltip: "O pior tempo de primeira resposta registrado no período.",
      icon: "Clock",
      accent: "rose",
    },
    {
      key: "avg_first_reply_client",
      label: "Tempo médio de 1ª resposta do cliente",
      value: fmtHms(curAvgClient),
      valueRaw: curAvgClient,
      description: "Média geral",
      delta: buildDelta(curAvgClient, priorAvgClient, true),
      tooltip: "Tempo médio que o cliente leva para responder o atendente.",
      icon: "User",
      accent: "amber",
    },
    {
      key: "total_open",
      label: "Total de casos em aberto",
      value: fmtCount(curTotalOpen),
      valueRaw: curTotalOpen,
      description: "No momento",
      delta: buildDelta(curTotalOpen, priorTotalOpen, true),
      tooltip: "Quantidade total de conversas abertas no momento.",
      icon: "Folder",
      accent: "indigo",
    },
    {
      key: "waiting_client",
      label: "Casos aguardando resposta do cliente",
      value: fmtCount(curWaitingClient),
      valueRaw: curWaitingClient,
      description: "No momento",
      delta: buildDelta(curWaitingClient, priorWaitingClient, false),
      tooltip: "Casos em que o atendente já respondeu e está esperando o cliente.",
      icon: "Hourglass",
      accent: "indigo",
    },
    {
      key: "avg_interaction_time",
      label: "Tempo médio total de interação",
      value: fmtHms(curInteraction),
      valueRaw: curInteraction,
      description: "Por cliente (concluídas)",
      delta: buildDelta(curInteraction, priorInteraction, true),
      tooltip: "Duração média do atendimento de ponta a ponta nas conversas concluídas.",
      icon: "Activity",
      accent: "indigo",
    },
    {
      key: "total_today",
      label: "Total de atendimentos do dia",
      value: fmtCount(curTodayCount),
      valueRaw: curTodayCount,
      description: "Hoje",
      delta: buildDelta(curTodayCount, priorTodayCount, false),
      tooltip: "Quantidade de conversas iniciadas hoje em todas as unidades.",
      icon: "ChartLine",
      accent: "emerald",
    },
  ];
};

// ── Performance por Smart Center ──────────────────────────────────────
/**
 * Para cada SC, agrega:
 *   - status (ok/warn/fail) — baseado em SLA (avgFirstReplyAttendant)
 *   - totalOpen
 *   - avgFirstReplyAttendant + ok flag
 *   - avgFirstReplyClient + ok flag
 *   - waitingClient
 *   - avgInteractionTime
 *   - totalToday
 *
 * SLA padrão pra "ok":
 *   - 1ª resposta atendente <= 5min
 *   - 1ª resposta cliente <= 8min
 */
const SLA_ATTENDANT_SEC = 5 * 60;
const SLA_CLIENT_SEC = 8 * 60;

const fmtHmsLocal = (sec) => {
  if (!Number.isFinite(sec) || sec <= 0) return "00:00:00";
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const computePerformanceBySmartCenter = ({ conversations, smartCenters, now }) => {
  // Indexa conversas por SC
  const bySc = new Map();
  for (const c of conversations) {
    if (!bySc.has(c.smartCenterId)) bySc.set(c.smartCenterId, []);
    bySc.get(c.smartCenterId).push(c);
  }

  return smartCenters.map((sc) => {
    const cs = bySc.get(sc.id) || [];
    const totalOpen = totalOpenCount(cs);
    const avgAttSec = avgFirstReplyAttendantSec(cs);
    const avgClSec = avgFirstReplyClientSec(cs);
    const waiting = waitingClientCount(cs);
    const interactionSec = avgInteractionTimeSec(cs);
    const today = totalTodayCount(cs, now);

    const avgAttOk = avgAttSec === 0 ? true : avgAttSec <= SLA_ATTENDANT_SEC;
    const avgClOk = avgClSec === 0 ? true : avgClSec <= SLA_CLIENT_SEC;
    // status: fail se 1ª resp atendente > 6 min, warn se 5-6 min, ok caso contrário
    let status = "ok";
    if (avgAttSec > SLA_ATTENDANT_SEC + 60) status = "fail";
    else if (avgAttSec > SLA_ATTENDANT_SEC) status = "warn";

    return {
      id: sc.id,
      name: sc.name,
      ascCode: sc.ascCode,
      companyId: sc.companyId,
      subsidiaryId: sc.subsidiaryId,
      status,
      totalOpen,
      avgFirstReplyAttendant: fmtHmsLocal(avgAttSec),
      avgFirstReplyAttendantSec: avgAttSec,
      avgFirstReplyAttendantOk: avgAttOk,
      avgFirstReplyClient: fmtHmsLocal(avgClSec),
      avgFirstReplyClientSec: avgClSec,
      avgFirstReplyClientOk: avgClOk,
      waitingClient: waiting,
      avgInteractionTime: fmtHmsLocal(interactionSec),
      avgInteractionTimeSec: interactionSec,
      totalToday: today,
    };
  });
};

// ── Gráficos: séries históricas mês a mês ─────────────────────────────
/**
 * Para cada um dos últimos 12 meses (now-11 até now inclusive), agrupa
 * as conversas e calcula:
 *   - openNoReply: count de no_reply iniciados naquele mês
 *   - totalOpen: count de conversas em aberto que iniciaram naquele mês
 *   - waitingClient: count aguardando cliente que iniciaram naquele mês
 *   - totalDaily: count total iniciado no mês (label do gráfico já diz "dia"
 *     mas mantemos contagem mensal pra leitura macro)
 *   - longestNoReplyAttendantSec: pior caso de no_reply do mês
 *   - avgFirstReplyAttendantSec, avgFirstReplyClientSec, longestFirstReplySec
 *   - avgInteractionSec
 */
export const computeHistorical = ({ conversations, now }) => {
  // Buckets de mês
  const months = [];
  const d = new Date(now);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  for (let i = 11; i >= 0; i--) {
    const x = new Date(d);
    x.setMonth(d.getMonth() - i);
    months.push({
      key: x.getTime(),
      label: x.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }).replace(".", "").replace(" de ", "/"),
      from: x.getTime(),
      to: new Date(x.getFullYear(), x.getMonth() + 1, 1).getTime(),
    });
  }

  // Bucket por mês
  const buckets = months.map((m) => ({ ...m, convs: [] }));
  for (const c of conversations) {
    for (const b of buckets) {
      if (c.startedAt >= b.from && c.startedAt < b.to) {
        b.convs.push(c);
        break;
      }
    }
  }

  const series = (computeFn) => buckets.map((b) => ({ month: b.label, value: computeFn(b.convs, b.from, b.to) }));

  return {
    openNoReply: series((cs) => openNoReplyCount(cs)),
    totalOpen: series((cs) => totalOpenCount(cs)),
    waitingClient: series((cs) => waitingClientCount(cs)),
    totalDaily: series((cs) => cs.length),
    longestNoReplyAttendantSec: series((cs, from, to) => {
      // Maior delay observado em conversas no_reply do mês
      let best = 0;
      const endRef = Math.min(now.getTime(), to);
      for (const c of cs) {
        if (c.status !== "no_reply") continue;
        const dur = endRef - c.firstClientAt;
        if (dur > best) best = dur;
      }
      return Math.round(best / 1000);
    }),
    avgFirstReplyAttendantSec: series((cs) => avgFirstReplyAttendantSec(cs)),
    avgFirstReplyClientSec: series((cs) => avgFirstReplyClientSec(cs)),
    longestFirstReplySec: series((cs) => longestFirstReply(cs).seconds),
    avgInteractionSec: series((cs) => avgInteractionTimeSec(cs)),
  };
};

// ── Casos abertos detalhados (drill-in unidade) ───────────────────────
export const getOpenCasesForUnit = ({ conversations, smartCenterId, now, limit = 50 }) => {
  const nowMs = now.getTime();
  return conversations
    .filter((c) => c.smartCenterId === smartCenterId && c.endedAt == null)
    .map((c) => {
      // Tempo sem resposta ao cliente:
      // - Se aguardando agente (cliente foi último): tempo desde a última msg do cliente
      // - Se no_reply: tempo desde firstClientAt
      // - Se waiting_client: agente já respondeu, então 0 (ou tempo desde ult msg do agente — semântica menos crítica)
      let noReplyMs = 0;
      let isCritical = false;
      if (c.status === "no_reply") {
        noReplyMs = nowMs - c.firstClientAt;
      } else if (c.status === "waiting_agent") {
        noReplyMs = nowMs - c.lastMsgAt;
      } else {
        noReplyMs = 0;
      }
      isCritical = noReplyMs > 5 * MIN;
      return {
        id: c.id,
        startedAt: new Date(c.startedAt).toISOString(),
        noReplyMs,
        noReplyDuration: fmtHmsLocal(noReplyMs / 1000),
        noReplyCritical: isCritical,
        agentId: c.agentId,
        customerId: c.customerId,
        conversationUrl: `/tickets/${c.id}`,
        status: c.status,
      };
    })
    .sort((a, b) => b.noReplyMs - a.noReplyMs)
    .slice(0, limit);
};

// ── SMS Alerts: counters dinâmicos ────────────────────────────────────
/**
 * Pra cada nível de alerta, conta quantas conversas TIVERAM um caso que
 * passou daquele tempo sem resposta. Cada nível conta apenas casos que
 * pertencem àquele bucket de tempo (não cumulativo).
 *
 * Buckets (minutos):
 *   1º: 5-10
 *   2º: 10-15
 *   3º: 15-20
 *   4º: 20-30
 *   5º: > 30
 */
export const computeSmsCounters = ({ conversations, now }) => {
  const nowMs = now.getTime();
  const counters = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  for (const c of conversations) {
    // Conta o "delay até primeira resposta do agente" ou, se ainda em
    // no_reply, o delay até agora.
    let delayMs = 0;
    if (c.firstAgentReplyAt != null) {
      delayMs = c.firstAgentReplyAt - c.firstClientAt;
    } else if (c.status === "no_reply") {
      delayMs = nowMs - c.firstClientAt;
    } else {
      continue;
    }
    const minutes = delayMs / 60000;
    if (minutes < 5) continue;
    if (minutes < 10) counters[1]++;
    else if (minutes < 15) counters[2]++;
    else if (minutes < 20) counters[3]++;
    else if (minutes < 30) counters[4]++;
    else counters[5]++;
  }
  return counters;
};

// ── Filtragem aplicada ao dataset ──────────────────────────────────────
/**
 * Aplica os filtros da UI (subsidiária, smartCenter, status, período)
 * ao array de conversas. Devolve o subset filtrado.
 *
 * Período (período preset):
 *   today: últimas 24h
 *   yesterday: 24h-48h atrás
 *   7d: últimos 7 dias
 *   30d: últimos 30 dias
 *   12m: últimos 365 dias
 *   custom: usa filters.dateRange { from, to }
 */
export const filterConversations = ({ conversations, filters, now }) => {
  const nowMs = now.getTime();
  const period = filters.period || "today";
  let fromMs = 0;
  let toMs = nowMs;
  switch (period) {
    case "today": fromMs = nowMs - DAY; break;
    case "yesterday": fromMs = nowMs - 2 * DAY; toMs = nowMs - DAY; break;
    case "7d": fromMs = nowMs - 7 * DAY; break;
    case "30d": fromMs = nowMs - 30 * DAY; break;
    case "12m": fromMs = nowMs - 365 * DAY; break;
    case "custom":
      if (filters.dateRange?.from) fromMs = new Date(filters.dateRange.from).getTime();
      if (filters.dateRange?.to) toMs = new Date(filters.dateRange.to).getTime();
      break;
    default: fromMs = nowMs - DAY;
  }

  return conversations.filter((c) => {
    if (c.startedAt < fromMs || c.startedAt > toMs) return false;
    if (filters.subsidiary && filters.subsidiary !== "all" && c.subsidiaryId !== filters.subsidiary) return false;
    if (filters.smartCenterId && filters.smartCenterId !== "all" && c.smartCenterId !== filters.smartCenterId) return false;
    return true;
  });
};

/**
 * Para o gráfico histórico (12m), aplicamos um conjunto MAIOR de filtros
 * (subsidiária e smartCenter), mas SEM cortar por período — o gráfico
 * mostra a evolução nos últimos 12 meses por design.
 */
export const filterConversationsForHistorical = ({ conversations, filters }) => {
  return conversations.filter((c) => {
    if (filters.subsidiary && filters.subsidiary !== "all" && c.subsidiaryId !== filters.subsidiary) return false;
    if (filters.smartCenterId && filters.smartCenterId !== "all" && c.smartCenterId !== filters.smartCenterId) return false;
    return true;
  });
};
